/**
 * LIVE SALES TICKER — admin-only widget that polls /admin/sales-feed every
 * 5 s and renders the most recent paid Stripe transactions plus running
 * totals (today + lifetime). Perfect for the "show me the money" moment
 * during an investor demo.
 */
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Activity, TrendingUp, DollarSign } from "lucide-react";
import { http } from "@/lib/http";

function timeAgo(iso) {
  if (!iso) return "—";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function maskEmail(e) {
  if (!e || !e.includes("@")) return "anon";
  const [n, d] = e.split("@");
  return `${n.slice(0, 2)}•••@${d}`;
}

export default function LiveSalesTicker() {
  const [data, setData] = useState({ rows: [], totals: { lifetime_usd: 0, today_usd: 0, count_paid: 0 } });
  const [error, setError] = useState(null);
  const [lastNewIds, setLastNewIds] = useState(new Set());

  useEffect(() => {
    let cancelled = false;
    let prevIds = new Set();

    const tick = async () => {
      try {
        const { data } = await http.get("/admin/sales-feed?limit=20");
        if (cancelled) return;
        setData(data);
        setError(null);
        // Detect new rows since last poll
        const newRowIds = new Set(
          data.rows.map((r) => r.session_id || r.id).filter((id) => !prevIds.has(id)),
        );
        if (prevIds.size > 0 && newRowIds.size > 0) {
          setLastNewIds(newRowIds);
          setTimeout(() => { if (!cancelled) setLastNewIds(new Set()); }, 4000);
        }
        prevIds = new Set(data.rows.map((r) => r.session_id || r.id));
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Feed unavailable");
      }
    };
    tick();
    const t = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  return (
    <div className="border border-white/10 bg-[#07080C] p-5 md:p-6" data-testid="admin-live-sales-ticker">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[#00E5FF] inline-flex items-center gap-2">
          <Activity size={12} />
          <span className="ring-dot" /> Live Sales · Stripe Webhook
        </div>
        {error && <span className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[#FF007F]">{error}</span>}
      </div>

      {/* Totals row */}
      <div className="mt-4 grid grid-cols-3 gap-px bg-white/10 border border-white/10">
        <div className="bg-[#0B0608] p-4">
          <div className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-white/45 inline-flex items-center gap-1.5">
            <DollarSign size={11} /> Today
          </div>
          <div className="mt-2 font-display font-black tracking-tighter text-2xl md:text-3xl text-cyan-glow" data-testid="ticker-today">
            ${data.totals.today_usd.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </div>
        </div>
        <div className="bg-[#0B0608] p-4">
          <div className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-white/45 inline-flex items-center gap-1.5">
            <TrendingUp size={11} /> Lifetime
          </div>
          <div className="mt-2 font-display font-black tracking-tighter text-2xl md:text-3xl text-white" data-testid="ticker-lifetime">
            ${data.totals.lifetime_usd.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </div>
        </div>
        <div className="bg-[#0B0608] p-4">
          <div className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-white/45">Paid #</div>
          <div className="mt-2 font-display font-black tracking-tighter text-2xl md:text-3xl text-[#FFD166]" data-testid="ticker-count">
            {data.totals.count_paid}
          </div>
        </div>
      </div>

      {/* Recent rows */}
      <div className="mt-5 max-h-[280px] overflow-y-auto" data-testid="ticker-rows">
        <AnimatePresence initial={false}>
          {data.rows.length === 0 ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="py-10 text-center font-mono-ui text-[11px] tracking-[0.28em] uppercase text-white/35"
            >
              No paid transactions yet. Sales appear here in real time.
            </motion.div>
          ) : (
            data.rows.map((r) => {
              const isNew = lastNewIds.has(r.session_id || r.id);
              return (
                <motion.div
                  key={r.session_id || r.id}
                  layout
                  initial={isNew ? { opacity: 0, x: -30, backgroundColor: "rgba(0,229,255,0.18)" } : { opacity: 1 }}
                  animate={{ opacity: 1, x: 0, backgroundColor: "rgba(0,0,0,0)" }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                  className="grid grid-cols-[110px_1fr_90px] md:grid-cols-[140px_1fr_120px] gap-3 items-center py-3 border-b border-white/5"
                  data-testid={`ticker-row-${r.session_id || r.id}`}
                >
                  <div className="font-display font-black tracking-tighter text-lg md:text-xl text-cyan-glow">
                    ${r.amount.toFixed(2)}
                  </div>
                  <div className="min-w-0">
                    <div className="font-mono-ui text-[10px] tracking-[0.18em] uppercase text-white/85 truncate">{r.label}</div>
                    <div className="font-mono-ui text-[10px] tracking-[0.15em] text-white/40 mt-1 truncate">{maskEmail(r.email)}</div>
                  </div>
                  <div className="text-right font-mono-ui text-[10px] tracking-[0.18em] uppercase text-white/45">
                    {timeAgo(r.updated_at)}
                  </div>
                </motion.div>
              );
            })
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
